import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api from "@/utils/api";
import { useCurrentWorkflow, useFetchPublicWorkflowBySlug, useWorkflowError } from "@/store";
import LoadingScreen from "@/components/LoadingScreen";
import { RapidWorkflowDataType } from "@/components/flow/shared/Deploy/documentation/types";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { useAuth } from "@/contexts/AuthContext";

export const HydratePublicWorkflow = ({ children }: { children: React.ReactNode }) => {
    const { shareableSlug } = useParams<{ shareableSlug: string }>();
    const fetchPublicWorkflowBySlug = useFetchPublicWorkflowBySlug();
    const navigate = useNavigate();
    const { guestAuthInitialized } = useAuth();

    const currentWorkflow = useCurrentWorkflow();
    const workflowError = useWorkflowError();

    const onHydrateWorkflowError = useCallback(
        (e: Error) => {
            alert(
                `There was an error loading the shared workflow: ${e}. You will be redirected to the home page.`
            );
            navigate("/");
        },
        [navigate]
    );

    const onHydrateWorkflowSuccess = useCallback(
        (workflow: RapidWorkflowDataType | null) => {
            if (!workflow) {
                onHydrateWorkflowError(new Error("No workflow found"));
            }
        },
        [onHydrateWorkflowError]
    );

    const handleFetchPublicWorkflowBySlug = useCallback(async () => {
        // Wait for guest auth to initialize before fetching
        if (!guestAuthInitialized) {
            return;
        }

        // For public workflows, we fetch by shareable slug without requiring authentication
        if (shareableSlug && currentWorkflow?.rapidData?.url !== shareableSlug) {
            await fetchPublicWorkflowBySlug(
                shareableSlug,
                api,
                onHydrateWorkflowSuccess,
                onHydrateWorkflowError
            );
        }
    }, [
        currentWorkflow,
        fetchPublicWorkflowBySlug,
        onHydrateWorkflowSuccess,
        onHydrateWorkflowError,
        shareableSlug,
        guestAuthInitialized
    ]);

    const {
        isLoading: isLoadingFetchWorkflowByUrl,
        error: errorFetchWorkflowByUrl,
        invoke: invokeFetchWorkflowByUrl
    } = useAsyncBoundOperation(handleFetchPublicWorkflowBySlug, { autoInvoke: true });

    const error = workflowError || errorFetchWorkflowByUrl;

    const handleErrorRetry = useCallback(() => {
        invokeFetchWorkflowByUrl();
    }, [invokeFetchWorkflowByUrl]);

    if (error) {
        return (
            <div className="flex h-full items-center justify-center p-4">
                <ErrorInfo error={error} onRetryClick={handleErrorRetry} />
            </div>
        );
    }

    if (!guestAuthInitialized || isLoadingFetchWorkflowByUrl || !currentWorkflow) {
        return <LoadingScreen />;
    }

    return <>{children}</>;
};
